import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Autocomplete,
  Tabs,
  Tab,
  styled,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import InventoryIcon from '@mui/icons-material/Inventory';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import { useAuth } from '../../../contexts/AuthContext';
import { accountingApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';
import AccountingDataTable from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import { useTranslation } from 'react-i18next';

// ─── Tipler ──────────────────────────────────────────────────────────────────

interface WarehouseStockEntry {
  id: string;
  productId: string;
  warehouseId: string;
  warehouseName?: string;
  quantity: number;
}

interface InventoryItem {
  id: string;
  name: string;
  barcode?: string;
  categoryId?: string | null;
  brandId?: string | null;
  stockCode?: string;
  /** Seçili depoya ait miktar — warehouseStocks[] dizisinden çekilir */
  quantity: number;
  unit?: string;
  purchasePrice?: number;
  purchaseCurrency?: string;
  salePrice?: number;
  saleCurrency?: string;
  warehouseStocks?: WarehouseStockEntry[];
}

interface Warehouse {
  id: string;
  name: string;
  isDefault?: boolean;
}

interface ApiResponse {
  items: any[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

// ─── Sabitler ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 400;

// ─── Styled ──────────────────────────────────────────────────────────────────

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

// ─── TabPanel ────────────────────────────────────────────────────────────────

function TabPanel({ children, value, index }: { children?: React.ReactNode; value: number; index: number }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// ─── Yardımcı ─────────────────────────────────────────────────────────────────

function getCurrencySymbol(currency = 'TRY') {
  if (currency === 'USD') return '$';
  if (currency === 'EUR') return '€';
  return '₺';
}

/**
 * Backend'den gelen item'daki warehouseStocks[] dizisinden
 * seçili depoya ait stok miktarını döndürür.
 */
function extractQuantity(data: any, warehouseId: string): number {
  const stocks: any[] =
    data.warehouseStocks ?? data.WarehouseStocks ?? [];
  return stocks
    .filter(
      (ws: any) =>
        (ws.warehouseId ?? ws.WarehouseId)?.toLowerCase() ===
        warehouseId.toLowerCase()
    )
    .reduce((sum: number, ws: any) => sum + Number(ws.quantity ?? ws.Quantity ?? 0), 0);
}

function mapItem(data: any, warehouseId: string): InventoryItem {
  return {
    id: data.id ?? data.Id,
    name: data.name ?? data.Name ?? '',
    barcode: data.barcode ?? data.Barcode ?? '',
    categoryId: data.categoryId ?? data.CategoryId ?? null,
    brandId: data.brandId ?? data.BrandId ?? null,
    stockCode: data.stockCode ?? data.StockCode ?? '',
    quantity: extractQuantity(data, warehouseId),
    unit: data.unit ?? data.Unit ?? 'adet',
    purchasePrice: data.purchasePrice ?? data.PurchasePrice ?? 0,
    purchaseCurrency: data.purchaseCurrency ?? data.PurchaseCurrency ?? 'TRY',
    salePrice: data.salePrice ?? data.SalePrice ?? 0,
    saleCurrency: data.saleCurrency ?? data.SaleCurrency ?? 'TRY',
    warehouseStocks: data.warehouseStocks ?? data.WarehouseStocks ?? [],
  };
}

// ─── Bileşen ──────────────────────────────────────────────────────────────────

const WarehouseItems: React.FC = () => {
  const { warehouses, getCategoryName, getBrandName } = useAuth();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  const [companyId, setCompanyId] = useState<string | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);

  // Aktif tab: 0 = stoğu olan, 1 = stoğu olmayan
  const [tabValue, setTabValue] = useState(0);

  // Her tab için bağımsız state
  const [itemsWithStock, setItemsWithStock] = useState<InventoryItem[]>([]);
  const [totalWithStock, setTotalWithStock] = useState(0);
  const [pageWithStock, setPageWithStock] = useState(1);
  const [loadingWithStock, setLoadingWithStock] = useState(false);

  const [itemsWithoutStock, setItemsWithoutStock] = useState<InventoryItem[]>([]);
  const [totalWithoutStock, setTotalWithoutStock] = useState(0);
  const [pageWithoutStock, setPageWithoutStock] = useState(1);
  const [loadingWithoutStock, setLoadingWithoutStock] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Race-condition koruması
  const fetchIdWithStock = useRef(0);
  const fetchIdWithoutStock = useRef(0);

  // ─── companyId yükle ────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) return;
      const userData = await firebaseDataService.getUserData(uid);
      if (userData) setCompanyId(userData.companyId || null);
    };
    load();
  }, []);

  // ─── Arama debounce ─────────────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // ─── "Stoğu olan" yükle ─────────────────────────────────────────────────────
  const loadWithStock = useCallback(async (page: number) => {
    if (!companyId || !selectedWarehouse) return;

    const currentFetch = ++fetchIdWithStock.current;
    setLoadingWithStock(true);
    setError(null);

    try {
      const response = await accountingApi.post<ApiResponse>(
        ACCOUNTING_API.INVENTORY_ITEMS_PAGED,
        {
          PageNumber: page,
          PageSize: PAGE_SIZE,
          WarehouseId: selectedWarehouse.id,
          SearchTerm: debouncedSearch || null,
          HideZeroStock: true,   // yalnızca quantity > 0
        }
      );

      if (currentFetch !== fetchIdWithStock.current) return;

      const data = response.data;
      const rawItems: any[] = data.items ?? (data as any).Items ?? [];
      const total: number = data.totalCount ?? (data as any).TotalCount ?? 0;

      setItemsWithStock(rawItems.map(d => mapItem(d, selectedWarehouse.id)));
      setTotalWithStock(total);
    } catch (err) {
      if (currentFetch !== fetchIdWithStock.current) return;
      console.error('[WarehouseItems] Stoğu olan yüklenirken hata:', err);
      setError(tt('Stoğu olan ürünler yüklenirken hata oluştu.', 'Error occurred while loading in-stock items.'));
    } finally {
      if (currentFetch === fetchIdWithStock.current) setLoadingWithStock(false);
    }
  }, [companyId, selectedWarehouse, debouncedSearch]);

  // ─── "Stoğu olmayan" yükle ──────────────────────────────────────────────────
  const loadWithoutStock = useCallback(async (page: number) => {
    if (!companyId || !selectedWarehouse) return;

    const currentFetch = ++fetchIdWithoutStock.current;
    setLoadingWithoutStock(true);
    setError(null);

    try {
      const response = await accountingApi.post<ApiResponse>(
        ACCOUNTING_API.INVENTORY_ITEMS_PAGED,
        {
          PageNumber: page,
          PageSize: PAGE_SIZE,
          WarehouseId: selectedWarehouse.id,
          SearchTerm: debouncedSearch || null,
          HidePositiveStock: true,  // yalnızca quantity = 0
        }
      );

      if (currentFetch !== fetchIdWithoutStock.current) return;

      const data = response.data;
      const rawItems: any[] = data.items ?? (data as any).Items ?? [];
      const total: number = data.totalCount ?? (data as any).TotalCount ?? 0;

      setItemsWithoutStock(rawItems.map(d => mapItem(d, selectedWarehouse.id)));
      setTotalWithoutStock(total);
    } catch (err) {
      if (currentFetch !== fetchIdWithoutStock.current) return;
      console.error('[WarehouseItems] Stoğu olmayan yüklenirken hata:', err);
      setError(tt('Stoğu olmayan ürünler yüklenirken hata oluştu.', 'Error occurred while loading out-of-stock items.'));
    } finally {
      if (currentFetch === fetchIdWithoutStock.current) setLoadingWithoutStock(false);
    }
  }, [companyId, selectedWarehouse, debouncedSearch]);

  // ─── Depo veya arama değişince ──────────────────────────────────────────────
  useEffect(() => {
    setPageWithStock(1);
    setPageWithoutStock(1);
    setItemsWithStock([]);
    setItemsWithoutStock([]);
    setTotalWithStock(0);
    setTotalWithoutStock(0);

    if (selectedWarehouse && companyId) {
      loadWithStock(1);
      loadWithoutStock(1);
    }
  }, [selectedWarehouse, companyId, debouncedSearch]);

  // ─── Sayfa değişimi (stoğu olan) ────────────────────────────────────────────
  useEffect(() => {
    if (pageWithStock > 1) loadWithStock(pageWithStock);
  }, [pageWithStock]);

  // ─── Sayfa değişimi (stoğu olmayan) ─────────────────────────────────────────
  useEffect(() => {
    if (pageWithoutStock > 1) loadWithoutStock(pageWithoutStock);
  }, [pageWithoutStock]);

  // ─── Tab değişimi ───────────────────────────────────────────────────────────
  const handleChangeTab = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // ─── Kolon tanımları ────────────────────────────────────────────────────────
  const inventoryColumns = useMemo<DataTableColumn<InventoryItem>[]>(() => [
    {
      id: 'name',
      label: tt('Ürün Adı', 'Item Name'),
      field: 'name',
      sortable: true,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <InventoryIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
          {row.name}
        </Box>
      ),
    },
    {
      id: 'stockCode',
      label: tt('Stok Kodu', 'Stock Code'),
      field: 'stockCode',
      sortable: true,
      render: (row) => <>{row.stockCode || '-'}</>,
    },
    {
      id: 'barcode',
      label: tt('Barkod', 'Barcode'),
      field: 'barcode',
      sortable: true,
      render: (row) => <>{row.barcode || '-'}</>,
    },
    {
      id: 'categoryId',
      label: tt('Kategori', 'Category'),
      sortable: true,
      render: (row) => <>{getCategoryName(row.categoryId)}</>,
    },
    {
      id: 'brandId',
      label: tt('Marka', 'Brand'),
      sortable: true,
      render: (row) => <>{getBrandName(row.brandId)}</>,
    },
    {
      id: 'quantity',
      label: tt('Miktar', 'Quantity'),
      field: 'quantity',
      sortable: true,
      render: (row) => (
        <Box
          component="span"
          sx={{
            color: row.quantity === 0 ? 'error.main' : 'inherit',
            fontWeight: row.quantity === 0 ? 'bold' : 'normal',
          }}
        >
          {row.quantity.toLocaleString(localeCode)}
        </Box>
      ),
    },
    {
      id: 'unit',
      label: tt('Birim', 'Unit'),
      render: (row) => <>{row.unit || tt('Adet', 'Piece')}</>,
    },
    {
      id: 'purchasePrice',
      label: tt('Alış Fiyatı', 'Purchase Price'),
      field: 'purchasePrice',
      sortable: true,
      render: (row) => (
        <>{`${(row.purchasePrice || 0).toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${getCurrencySymbol(row.purchaseCurrency)}`}</>
      ),
    },
    {
      id: 'salePrice',
      label: tt('Satış Fiyatı', 'Sale Price'),
      field: 'salePrice',
      sortable: true,
      render: (row) => (
        <>{`${(row.salePrice || 0).toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${getCurrencySymbol(row.saleCurrency)}`}</>
      ),
    },
  ], [getCategoryName, getBrandName, isTr, localeCode]);

  // ─── Tablo render yardımcısı ─────────────────────────────────────────────────
  // AccountingDataTable: page 0-tabanlı, onPageChange(0-tabanlı page, pageSize) çağırır
  const renderTable = (
    items: InventoryItem[],
    totalCount: number,
    page: number,             // 1-tabanlı (bileşen içi state)
    onPageChange: (newPage: number) => void,  // 1-tabanlı page alır
    loading: boolean,
  ) => (
    <AccountingDataTable<InventoryItem>
      columns={inventoryColumns}
      rows={items}
      getRowId={(row) => row.id}
      loading={loading}
      defaultSortField="name"
      defaultSortDirection="asc"
      emptyMessage={tt('Ürün bulunamadı.', 'No item found.')}
      emptyIcon={<InventoryIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
      ariaLabel={tt('depo ürünleri tablosu', 'warehouse products table')}
      columnResizeStorageKey="warehouse-items"
      serverSidePagination
      totalCount={totalCount}
      page={page - 1}          // 0-tabanlıya çevir
      pageSize={PAGE_SIZE}
      onPageChange={(p0based) => onPageChange(p0based + 1)}  // 1-tabanlıya çevir
    />
  );

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: '100%' }}>
      <HeaderContainer>
        <Typography variant="h5" component="h1" fontWeight="bold">
          {tt('Depo Ürünleri', 'Warehouse Products')}
        </Typography>
      </HeaderContainer>

      {/* Filtre satırı */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Autocomplete
          size="small"
          options={warehouses}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedWarehouse}
          onChange={(_, newValue) => {
            setSelectedWarehouse(newValue);
            setTabValue(0);
            setSearchTerm('');
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={tt('Depo Seçin', 'Select Warehouse')}
              size="small"
              sx={{ width: 250 }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <WarehouseIcon fontSize="small" />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <TextField
          size="small"
          placeholder={tt('Ürün Ara...', 'Search Item...')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 250 }}
          disabled={!selectedWarehouse}
        />
      </Box>

      {/* Seçili depo bilgisi */}
      {selectedWarehouse && (
        <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <Typography variant="subtitle1">
            {tt('Seçilen Depo:', 'Selected Warehouse:')} <strong>{selectedWarehouse.name}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {tt('Bu depoya ait ürünler listeleniyor. Farklı bir depo seçmek için yukarıdaki depo seçim kutusunu kullanabilirsiniz.', 'Products belonging to this warehouse are listed. To choose a different warehouse, use the warehouse selector above.')}
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Depo seçilmeden boş ekran */}
      {!selectedWarehouse ? (
        <Box
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '400px',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)',
            borderRadius: 2,
          }}
        >
          <WarehouseIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" component="h2" gutterBottom color="primary.main" fontWeight="bold">
            {tt('Depo Ürünleri', 'Warehouse Products')}
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            {tt('Listelemek istediğiniz ürünlerin deposunu seçiniz lütfen.', 'Please select the warehouse of products you want to list.')}
          </Typography>
        </Box>
      ) : (
        <Box>
          {/* Tablar */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleChangeTab}
              aria-label={tt('stok durumu tabları', 'stock status tabs')}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab
                label={
                  loadingWithStock
                    ? tt('Stoğu Olan Ürünler (yükleniyor...)', 'In-Stock Items (loading...)')
                    : `${tt('Stoğu Olan Ürünler', 'In-Stock Items')} (${totalWithStock})`
                }
                id="inventory-tab-0"
                aria-controls="inventory-tabpanel-0"
              />
              <Tab
                label={
                  loadingWithoutStock
                    ? tt('Stoğu Olmayan Ürünler (yükleniyor...)', 'Out-of-Stock Items (loading...)')
                    : `${tt('Stoğu Olmayan Ürünler', 'Out-of-Stock Items')} (${totalWithoutStock})`
                }
                id="inventory-tab-1"
                aria-controls="inventory-tabpanel-1"
              />
            </Tabs>
          </Box>

          {/* Stoğu Olan */}
          <TabPanel value={tabValue} index={0}>
            {loadingWithStock && itemsWithStock.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <CircularProgress />
              </Box>
            ) : itemsWithStock.length === 0 ? (
              <Alert severity="info">{tt('Bu depoda stoğu olan ürün bulunmamaktadır.', 'No in-stock item found in this warehouse.')}</Alert>
            ) : (
              renderTable(
                itemsWithStock,
                totalWithStock,
                pageWithStock,
                setPageWithStock,
                loadingWithStock,
              )
            )}
          </TabPanel>

          {/* Stoğu Olmayan */}
          <TabPanel value={tabValue} index={1}>
            {loadingWithoutStock && itemsWithoutStock.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <CircularProgress />
              </Box>
            ) : itemsWithoutStock.length === 0 ? (
              <Alert severity="info">{tt('Bu depoda stoğu olmayan ürün bulunmamaktadır.', 'No out-of-stock item found in this warehouse.')}</Alert>
            ) : (
              renderTable(
                itemsWithoutStock,
                totalWithoutStock,
                pageWithoutStock,
                setPageWithoutStock,
                loadingWithoutStock,
              )
            )}
          </TabPanel>
        </Box>
      )}
    </Box>
  );
};

export default WarehouseItems;
